/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useContext, useEffect, useRef } from "react";
import {
    Breadcrumb,
    Typography,
    Table,
    Button,
    Form,
    Input,
    DatePicker,
    Card,
    Select,
    Modal,
} from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export default function AddHoliday() {
    const isMobile = window.innerWidth < 768;

    const { authData, backUrl } = useContext(AuthContext);

    const [holidayData, setHolidayData] = useState([]);
    const [holidayOptions, setHolidayOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    // Filters
    const [selectedYear, setSelectedYear] = useState(null);
    const [month, setMonth] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [holidayName, setHolidayName] = useState(null);

    // Status tab: "pending" | "approved"
    const [statusTab, setStatusTab] = useState("pending");

    // UI
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showAddSection, setShowAddSection] = useState(false);

    // Edit modal
    const [isEdit, setIsEdit] = useState(false);
    const [editData, setEditData] = useState(null);
    const [form] = Form.useForm();

    const handleApproveSelected = async () => {
        if (selectedRowKeys.length === 0) return;

        try {
            setLoading(true);

            const res = await axios.post(
                `${backUrl}/api/hr/holidays/approve-holidays/`,
                { holiday_ids: selectedRowKeys },
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            // Success or informational messages
            if (res.data?.message) {
                toast.success(res.data.message);
            } else {
                toast.success("Selected holidays approved!");
            }

            setSelectedRowKeys([]);
            fetchHolidays();
        } catch (err) {
            console.error("Approval error:", err);

            if (err.response) {
                const data = err.response.data;

                if (data.message) {
                    toast.error(data.message);
                }
                else if (typeof data === "object") {
                    Object.entries(data).forEach(([key, value]) => {
                        toast.error(`${key}: ${Array.isArray(value) ? value.join(", ") : value}`);
                    });
                } else {
                    toast.error(data.toString());
                }
            } else if (err.request) {
                toast.error("No response from server!");
            } else {
                toast.error(err.message || "Failed to approve holidays!");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedRowKeys.length === 0) return;

        try {
            setLoading(true);

            const res = await axios.request({
                url: `${backUrl}/api/hr/holidays/delete-year/`,
                method: "delete",
                headers: { Authorization: `Bearer ${authData?.access}` },
                data: { holiday_ids: selectedRowKeys },
            });

            if (res.data?.message) {
                toast.success(res.data.message);
            } else {
                toast.success("Selected holidays deleted!");
            }

            setSelectedRowKeys([]);
            fetchHolidays();
        } catch (err) {
            console.error("Delete error:", err);

            if (err.response?.data) {
                const data = err.response.data;
                if (typeof data === "object") {
                    Object.entries(data).forEach(([key, value]) => {
                        toast.error(
                            `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
                        );
                    });
                } else {
                    toast.error(data.toString());
                }
            } else if (err.request) {
                toast.error("No response from server!");
            } else {
                toast.error(err.message || "Failed to delete holidays!");
            }
        } finally {
            setLoading(false);
        }
    };

    /** Fetch holidays */
    const fetchHolidays = async ({
        yearOverride,
        monthOverride,
        fromOverride,
        toOverride,
        nameOverride,
    } = {}) => {
        setLoading(true);
        try {
            const y = yearOverride ?? selectedYear;
            const m = monthOverride ?? month;
            const f = fromOverride ?? fromDate;
            const t = toOverride ?? toDate;
            const n = nameOverride ?? holidayName;

            const params = {};
            if (y) params.from_year = y;
            if (m) params.month = m;
            if (f) params.from_date = f.format("YYYY-MM-DD");
            if (t) params.to_date = t.format("YYYY-MM-DD");
            if (n) params.holiday_name = n;

            const res = await axios.get(`${backUrl}/api/hr/holidays/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
                params,
            });

            let list = res.data.results
                ? res.data.results
                : (Array.isArray(res.data) ? res.data : []);

            // Normalize dates
            list = list.map((item) => ({
                ...item,
                holiday_date: item.holiday_date
                    ? dayjs(item.holiday_date).format("YYYY-MM-DD")
                    : null,
            }));

            // ✅ No need for client-side filtering (backend already handles it)
            setHolidayData(list);

            // Populate holiday name options
            const uniqueNames = [
                ...new Set(list.map((h) => h.holiday_name).filter(Boolean)),
            ];
            setHolidayOptions(uniqueNames);
        } catch (error) {
            console.error("fetchHolidays error:", error);
            toast.error("Failed to fetch holidays!");
            setHolidayData([]);
        } finally {
            setLoading(false);
        }
    };


    // Fetch on filter changes
    useEffect(() => {
        if ((fromDate && !toDate) || (!fromDate && toDate)) return;
        fetchHolidays();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, month, fromDate, toDate, holidayName]);

    // Clear selection whenever the Pending/Approved tab changes
    useEffect(() => {
        setSelectedRowKeys([]);
    }, [statusTab]);

    /** Derived: split holidays into pending / approved */
    const isApproved = (record) =>
        Boolean(record.approved_by_name) || Boolean(record.approved_on);

    const pendingHolidays = React.useMemo(
        () => holidayData.filter((item) => !isApproved(item)),
        [holidayData]
    );

    const approvedHolidays = React.useMemo(
        () => holidayData.filter((item) => isApproved(item)),
        [holidayData]
    );

    const displayedHolidays = React.useMemo(
        () => (statusTab === "pending" ? pendingHolidays : approvedHolidays),
        [statusTab, pendingHolidays, approvedHolidays]
    );

    /** Edit Handlers */
    const handleEdit = (record) => {
        setEditData(record);
        setIsEdit(true);
        form.setFieldsValue({
            holiday_name: record.holiday_name,
            holiday_date: record.holiday_date
                ? dayjs(record.holiday_date, "YYYY-MM-DD")
                : null,
            year: record.year,
            holiday_day: record.holiday_day,
        });
    };

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                id: editData.id,
                holiday_date: values.holiday_date.format("YYYY-MM-DD"),
            };

            await axios.put(`${backUrl}/api/hr/holidays/${editData.id}/`, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            toast.success("Holiday updated");
            setIsEdit(false);
            fetchHolidays();
        } catch (err) {
            console.error("update error", err);
            toast.error("Failed to update holiday");
        }
    };

    /** Add Holiday Form */
    const AddHolidayForm = () => {
        const [f] = Form.useForm();
        const [submitting, setSubmitting] = useState(false);

        const onFinish = async (values) => {
            setSubmitting(true);
            try {
                const payload = {
                    year: values.year,
                    holiday_name: values.holiday_name,
                    holiday_day: values.holiday_day,
                    holiday_date: values.holiday_date.format("YYYY-MM-DD"),
                };

                await axios.post(`${backUrl}/api/hr/holidays/`, payload, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });

                toast.success("Holiday added");
                f.resetFields();
                fetchHolidays();
                setShowAddSection(false);
            } catch (err) {
                console.error("add error", err);
                toast.error("Failed to add holiday");
            } finally {
                setSubmitting(false);
            }
        };

        const handleDateChange = (date) => {
            if (date) {
                f.setFieldsValue({
                    year: String(date.year()),
                    holiday_day: date.format("dddd"),
                });
            } else {
                f.setFieldsValue({ year: "", holiday_day: "" });
            }
        };



        return (
            <div style={{ maxWidth: 800, margin: "0 auto", padding: "5px" }}>
                <ToastContainer
                    position="top-right"
                    autoClose={5000} // auto close after 5s
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        padding: 24,
                    }}
                >
                    <div style={{ textAlign: "center", marginBottom: 18, }}>
                        {/* <Title level={4} style={{ color: "#0e497b", marginBottom: 4 }}>
                            Add Holiday
                        </Title>
                        <Text type="secondary">
                            Fill in the details to add a new holiday
                        </Text> */}
                    </div>

                    <Form
                        form={f}
                        layout="vertical"
                        onFinish={onFinish}
                        requiredMark={false}
                    >
                        <Form.Item
                            name="holiday_name"
                            label="Holiday Name"
                            rules={[{ required: true, message: "Please enter holiday name" }]}
                        >
                            <Input placeholder="Enter holiday name" />
                        </Form.Item>

                        <Form.Item
                            name="holiday_date"
                            label="Holiday Date"
                            rules={[
                                { required: true, message: "Please select holiday date" },
                            ]}
                        >
                            <DatePicker
                                format="DD/MM/YYYY"
                                style={{ width: "100%" }}
                                onChange={handleDateChange}
                            />
                        </Form.Item>

                        <Form.Item name="year" label="Year">
                            <Input disabled placeholder="Auto-filled year" />
                        </Form.Item>

                        <Form.Item name="holiday_day" label="Holiday Day">
                            <Input disabled placeholder="Auto-filled day" />
                        </Form.Item>

                        <Form.Item style={{ marginTop: 12 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitting}
                                block
                                style={{ backgroundColor: "#0e497b", borderRadius: 8 }}
                            >
                                Add Holiday
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        );
    };

    /** Filters */
    const onMonthChange = (date) => {
        if (!date) {
            setMonth(null);
            return;
        }
        setMonth(date.month() + 1);
        if (!selectedYear) setSelectedYear(String(date.year()));
    };

    const FilterHolidays = () => {
        const monthValue = month
            ? dayjs(
                `${selectedYear || dayjs().year()}-${String(month).padStart(
                    2,
                    "0"
                )}`,
                "YYYY-MM"
            )
            : null;

        return (
            <div
                className="pt-2"
                style={{ gap: 12, display: 'flex', flexDirection: isMobile ? "column" : 'row', justifyContent: 'center', alignItems: 'center' }}
            >
                <DatePicker
                    picker="year"
                    placeholder="Select Year"
                    value={selectedYear ? dayjs(selectedYear, "YYYY") : undefined}
                    onChange={(date, dateString) => setSelectedYear(dateString || null)}
                    style={{ width: 180 }}
                />

                <DatePicker
                    picker="month"
                    placeholder="Select Month"
                    value={monthValue || undefined}
                    onChange={onMonthChange}
                    style={{ width: 180 }}
                />

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    value={fromDate || undefined}
                    onChange={(date) => setFromDate(date)}
                    style={{ width: 180 }}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    value={toDate || undefined}
                    onChange={(date) => setToDate(date)}
                    style={{ width: 180 }}
                />

                <Select
                    showSearch
                    allowClear
                    placeholder="Select Holiday Name"
                    value={holidayName || undefined}
                    onChange={(value) => setHolidayName(value || null)}
                    style={{ width: 220 }}
                    filterOption={(input, option) =>
                        option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {holidayOptions.map((name) => (
                        <Select.Option key={name} value={name}>
                            {name}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        );
    };

    /** Pending / Approved status tabs */
    const StatusTabs = () => {
        const baseStyle = {
            borderRadius: 8,
            fontWeight: 600,
            minWidth: 140,
        };

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 12,
                    marginBottom: 16,
                }}
            >
                <Button
                    style={{
                        ...baseStyle,
                        backgroundColor: statusTab === "pending" ? "#0e497b" : "#fff",
                        color: statusTab === "pending" ? "#fff" : "#0e497b",
                        border: "1px solid #0e497b",
                    }}
                    onClick={() => setStatusTab("pending")}
                >
                    Pending ({pendingHolidays.length})
                </Button>

                <Button
                    style={{
                        ...baseStyle,
                        backgroundColor: statusTab === "approved" ? "#0e497b" : "#fff",
                        color: statusTab === "approved" ? "#fff" : "#0e497b",
                        border: "1px solid #0e497b",
                    }}
                    onClick={() => setStatusTab("approved")}
                >
                    Approved ({approvedHolidays.length})
                </Button>
            </div>
        );
    };

    /** Table */
    const HolidayTable = () => {
        const columns = [
            { title: "Year", dataIndex: "year", key: "year" },
            {
                title: "Month",
                dataIndex: "holiday_date",
                key: "month",
                render: (date) => (date ? dayjs(date).format("MMMM") : "-"),
            },
            { title: "Day", dataIndex: "holiday_day", key: "holiday_day" },
            {
                title: "Date",
                dataIndex: "holiday_date",
                key: "holiday_date"
            },
            { title: "Name", dataIndex: "holiday_name", key: "holiday_name" },
            ...(statusTab === "approved"
                ? [
                    {
                        title: "Approved By",
                        dataIndex: "approved_by_name",
                        key: "approved_by_name",
                        render: (val) => val || "-",
                    },
                    {
                        title: "Approved On",
                        dataIndex: "approved_on",
                        key: "approved_on",
                        render: (val) =>
                            val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-",
                    },
                ]
                : []),
            {
                title: "Update",
                key: "update",
                render: (_, record) => (
                    <EditOutlined
                        style={{ color: "#0e497b", fontSize: 18, cursor: "pointer" }}
                        onClick={() => handleEdit(record)}
                    />
                ),
            },
        ];

        const rowSelection = {
            selectedRowKeys,
            preserveSelectedRowKeys: true,
            onChange: (newKeys) => {
                setSelectedRowKeys(newKeys);
            },
        };

        return (
            <>
                <center>
                    <FilterHolidays />
                </center>
                <br />

                <StatusTabs />

                {
                    (statusTab === "pending" && selectedRowKeys.length !== 0) && (
                        <><Button
                            type="primary"
                            style={{ backgroundColor: "#149a0cff", color: "#fff", marginBottom: 12 }}
                            disabled={selectedRowKeys.length === 0}
                            onClick={handleApproveSelected}
                        >
                            Approve Selected
                        </Button>
                            {/* <Button
                            type="primary"
                            style={{ backgroundColor: "#9f1515ff", color: "#fff", marginBottom: 12, marginLeft: 10 }}
                            disabled={selectedRowKeys.length === 0}
                            onClick={handleDeleteSelected}
                        >
                                Delete Selected
                            </Button> */}
                        </>
                    )
                }




                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={displayedHolidays}
                    rowKey="id"
                    loading={loading}
                    bordered
                    pagination={pagination}
                    onChange={(newPagination) => {
                        setPagination(newPagination);
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{ backgroundColor: "#0e497b", color: "white" }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    scroll={{ x: "max-content" }}
                    style={{ width: "100%" }}
                />

                <Modal
                    title="Edit Holiday"
                    open={isEdit}
                    onCancel={() => setIsEdit(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setIsEdit(false)}>
                            Cancel
                        </Button>,
                        <Button
                            key="update"
                            type="primary"
                            style={{ backgroundColor: "#0e497b", color: "#fff" }}
                            onClick={handleUpdate}
                        >
                            Update
                        </Button>,
                    ]}
                    centered
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="holiday_name"
                            label="Holiday Name"
                            rules={[{ required: true, message: "Please enter holiday name" }]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="holiday_date"
                            label="Holiday Date"
                            rules={[
                                { required: true, message: "Please select holiday date" },
                            ]}
                        >
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>

                        <Form.Item name="year" label="Year">
                            <Input disabled />
                        </Form.Item>

                        <Form.Item name="holiday_day" label="Holiday Day">
                            <Input disabled />
                        </Form.Item>
                    </Form>
                </Modal>
            </>
        );
    };

    return (
        <div>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Breadcrumb style={{ marginBottom: 16 }}>
                <Breadcrumb.Item>
                    <a
                        id="bread-acr"
                        style={{ color: "#0e497b" }}
                        onClick={() => {
                            setShowAddSection(false);
                        }}
                    >
                        Holidays
                    </a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <a
                        id="bread-acr"
                        style={{ color: "#0e497b" }}
                        onClick={() => {
                            setShowAddSection(true);
                        }}
                    >
                        Add Holiday
                    </a>
                </Breadcrumb.Item>
            </Breadcrumb>

            {showAddSection ? (
                <AddHolidayForm />
            ) : (
                <>
                    <center>
                        <h5>Holiday List</h5>
                    </center>
                    <HolidayTable />
                </>
            )}
        </div>
    );
}